import { cloneDeep, isEqual, last } from 'lodash';
import React, { Component } from 'react';
import analyticService from '../../../services/analytics';
import { dateService } from '../../../services/date';
import { session } from '../../../services/session';
import graphIcon from '../../../images/icons/graph.svg';
import { getClass, patchState } from '../../../utils/component-helpers';

import { buildQueryString, scrollPage } from '../common/utils';
import { Columns } from './column-service';
import { Granularities, Networks } from './models';
import megatableService from './megatable-service';

import Breadcrumbs from './breadcrumbs';
import ColumnsDropdown from '../dropdowns/columns-dropdown';
import DatePicker from '../date/date-picker';
import GranularitiesDropdown from '../dropdowns/granularities-dropdown';
import { Loader, NoResult } from '../../misc/loader-utils';
import PartnerDropdown from '../dropdowns/partner-dropdown';
import NetworksDropdown from '../dropdowns/networks-dropdown';
import Pagination from '../common/pagination';
import ReportDropdown from '../dropdowns/report-dropdown';
import StatTable from './stat-table';
import MegatableGraph from './megatable-graph';

import './megatable.scss';

class Megatable extends Component {
  constructor(props, statType, tableName, disableEditUrl, orgType) {
    super(props);
    patchState(this);

    this.statType = statType;
    this.tableName = tableName;
    this.disableEditUrl = disableEditUrl || false;
    this.orgType = orgType || 'publishers';
    this.partnerType = this.orgType === 'publishers' ? 'merchants' : 'publishers';
    this.formattedOrgType = this.partnerType.charAt(0).toUpperCase() + this.partnerType.slice(1);

    // Need to handle multiple row identifiers now that we graph more than just stories
    // edit_id: Stories tables
    // auction_id: Recommendations tables
    this.rowIdentifier = this.statType === 'merchantRecommendation' ? 'auction_id' : 'edit_id';

    this.AnalyticService = analyticService;
    this.columnService = new Columns();
    this.service = megatableService;
    this.Session = session;

    this.granularities = new Granularities().defaultGranularities;
    this.networks = new Networks(this.statType).defaultNetworks;

    this.allColumns = this.columnService.getColumnsForTable(this.statType, this.Session.isOrgAdmin);
    this.staticFields = this.columnService.staticFieldsByStatType[this.statType];
    this.sortDisabledFields = this.columnService.getSortDisabledFields(this.statType);
    this.defaultFields = this.columnService.getDefaultFields(this.statType);
    this.defaultAscendingFields = this.columnService.defaultAscendingFields;

    const savedParams = this.Session.dashboardParams;

    const columns = this.columnService.setColumnsSelected(
      this.allColumns,
      this.statType,
      savedParams,
    );

    this.maxGraphedRows = 3;
    this.defaultGraphedRows = 2;
    this.lastQueryParams = null;

    const currentDate = dateService.getCurrentDateTime();
    const defaultSort = this.columnService.getDefaultSortOptions(this.statType);

    this.state = {
      stats: null,
      totals: null,

      // Dropdown variables
      columns,
      granularities: this.granularities.slice(),
      networks: this.networks.slice(),
      merchants: [],
      publishers: [],

      // Query params
      params: {
        date_from: dateService.formatDate(
          dateService.getStartOf(null, 'month'), // start of this month
        ),
        date_to: dateService.formatDate(currentDate),
        order_by: defaultSort.order_by,
        direction: defaultSort.direction,
      },

      // State variables
      loading: true,
      showNoResult: false,

      // Pagination variables
      page: 1,
      perPage: 50,
      rowCounts: { visible: 0, all: 0 },
      totalItems: 0,

      // Graph variables
      graphedRows: [],
      graphStats: null,
      showGraph:
        (this.statType === 'article' && !this.service.breadcrumbClicked) ||
        ['merchantArticle', 'merchantRecommendation'].includes(this.statType),
      queriedGraphedIds: [],
    };
    this.defaultState = this.state;
  }

  async componentDidMount() {
    // Reset breadcrumbs if we navigated to this table via nav-bar
    if (!this.service.breadcrumbClicked) {
      this.service.breadcrumbs = [];
    } else {
      this.service.breadcrumbClicked = false;
    }

    this.AnalyticService.trackEvent('Mega Tables', 'Load', this.tableName);
    this.orgId = ['merchantArticle', 'merchantRecommendation', 'merchantTopPublisher'].includes(
      this.statType,
    )
      ? this.props.match.params.merchantId
      : this.props.match.params.publisherId;
    this._isMounted = true;

    this.updateColumnList();
    await this.queryOrgPartners();
    await this.getQueryParams();
    await this.queryStats(true, true);
    await this.queryEditGraphStats(true);
    this.updateQueryParams();

    this.queryTotals();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  async componentDidUpdate(prevProps, prevState) {
    // Reset table if the user clicked nav-sidebar
    if (this.lastQueryParams && this.props.location.search === '') {
      this.lastQueryParams = null;
      this.setState(this.defaultState, () => {
        this.componentDidMount();
      });
      return;
    }

    if (!isEqual(prevProps.flags, this.props.flags)) {
      this.updateColumnList();
    }

    if (
      !isEqual(prevState.networks, this.state.networks) ||
      !isEqual(prevState.merchants, this.state.merchants) ||
      !isEqual(prevState.publishers, this.state.publishers) ||
      prevProps.isAdminView !== this.props.isAdminView
    ) {
      this.setState({ graphStats: null });
      await this.queryStats(true);
      this.updateQueryParams();
      this.queryTotals();
      this.queryEditGraphStats(true);
    } else if (prevState.perPage !== this.state.perPage || prevState.page !== this.state.page) {
      await this.queryStats();
      this.updateQueryParams();
    }

    this.checkForEditQuery();
  }

  resetTableScrolls() {
    scrollPage('.base-megatable', 'scrollTop', 0);
    scrollPage('.base-megatable', 'scrollLeft', 0);
  }

  /* Stat functions */
  async queryStats(resetSearch = false, firstMount = false, sortChange = false) {
    // resetSearch: true if params have changed and we need to make an API request
    this.setState({
      loading: true,
      totalItems: 0,
    });
    // Reset table scrolls since the spinning loader doesn't scroll horizontally with the table
    this.resetTableScrolls();
    const params = this.getRequestParams();

    return this.service
      .getStats(this.orgId, this.statType, this.state.page, this.state.perPage, params, resetSearch)
      .then(({ stats, totalItems }) => {
        return this.onStatQuerySuccess(
          stats,
          totalItems,
          params,
          firstMount,
          resetSearch,
          sortChange,
        );
      })
      .then(() => {
        return this.setState({ loading: false });
      });
  }

  queryEditGraphStats(resetSearch = false) {
    if (
      (this.statType !== 'article' || !this.state.showGraph) &&
      !['merchantArticle', 'merchantRecommendation'].includes(this.statType)
    ) {
      return;
    }
    if (this.state.graphStats !== null) {
      this.setState({ graphStats: null });
    }

    const promises = [];
    const { graphedRows } = this.state;
    graphedRows.forEach(edit => {
      const paramsCopy = this.getEditGraphRequestParams(edit.edit_id, edit.auction_id);
      const { granularity } = paramsCopy;
      /*
       * Weekly and yearly stats are aggregated in frontend,
       * make API call with smaller granularity
       */
      if (granularity === 'weekly' || granularity === 'yearly') {
        paramsCopy.granularity = granularity === 'weekly' ? 'daily' : 'monthly';
      }
      promises.push(
        this.service.queryEditStats(this.orgId, `${this.statType}_graph`, paramsCopy, resetSearch),
      );
    });

    // Return for unit testing
    return Promise.all(promises).then(dataArr => {
      const graphStats = dataArr.map((data, index) => {
        const name =
          this.statType === 'merchantRecommendation'
            ? graphedRows[index].recommended_product
            : graphedRows[index].edit_name;
        return {
          name,
          data,
        };
      });
      this.setState({ graphStats });
    });
  }

  queryTotals() {
    // Set total values to dashes until the request finishes
    const totals = {};
    this.state.columns.forEach(columnObj => {
      totals[columnObj.name] = '-';
    });
    this.setState({ totals });

    const params = this.getRequestParams(true);
    this.service.loadStatTotals(this.orgId, this.statType, params).then(stats => {
      this.setState({ totals: stats });
    });
  }

  async queryOrgPartners() {
    await this.service.getOrgPartners(this.partnerType).then(partners => {
      // Mutate state to prevent multiple simultaneous stat queries
      this.state[this.partnerType] = partners;
    });
  }

  async onStatQuerySuccess(stats, totalItems, params, firstMount, resetSearch, sortChange) {
    let paramUpdate = null;
    let graphedRows = [];
    let merchants = cloneDeep(this.state.merchants);
    if (['article', 'merchantArticle', 'merchantRecommendation'].includes(this.statType)) {
      graphedRows = await this.filterGraphedRows(stats, firstMount, resetSearch, sortChange);
    } else if (this.statType === 'merchant' && params.search_query) {
      // Filters response to show only merchants displayed in the dropdown
      const dropdownIds = merchants.map(merch => merch.merch_id);
      stats = stats.filter(merch => dropdownIds.includes(merch.merch_id));

      const merchIds = stats.map(merch => merch.merch_id);
      merchants = merchants.map(merch => {
        merch.selected = merchIds.includes(merch.merch_id);
        return merch;
      });
    }

    // Change sort to clicks if no revenue
    if (
      session.userRole === 'merchants' &&
      this.state.params.order_by === 'total_attributed_revenue' &&
      stats.length &&
      this.statType !== 'merchantTopPublisher'
    ) {
      const firstRowRevenue = parseFloat(stats[0].total_attributed_revenue);
      paramUpdate = firstRowRevenue ? null : { ...this.state.params, order_by: 'clicks' };
    }

    // Kick off a call to fetch the next page so it will be preloaded
    const nextPage = this.service.limit / this.state.perPage + this.state.page;
    this.service.getStats(this.orgId, this.statType, nextPage, this.state.perPage, params);

    return this.setState(
      {
        stats,
        showNoResult: !stats.length,
        totalItems,
        rowCounts: { visible: stats.length, all: totalItems },
        graphedRows,
        merchants,
        params: paramUpdate || this.state.params,
      },
      async () => {
        if (paramUpdate) {
          this.updateQueryParams();
          await this.queryStats(true, false, true);
          this.queryEditGraphStats(true);
        }
      },
    );
  }

  filterGraphedRows(stats, firstMount, resetSearch, sortChange) {
    const queryIds = this.state.queriedGraphedIds;

    // Check for query params
    if (firstMount && queryIds.length) {
      return this.getGraphedRows(stats, queryIds, resetSearch);
    }
    if (firstMount || sortChange) {
      const newRows = stats.slice(0, this.defaultGraphedRows);
      return Promise.resolve(newRows);
    }

    // Check if graphed rows are in the stat cache
    const cache = this.service.statCacheKeyedOnType[this.statType].pages || [];
    const queriedRows = Object.values(cache).reduce((agg, e) => agg.concat(e), []);
    return this.getGraphedRows(queriedRows, this.state.graphedRows, resetSearch);
  }

  // Return corresponding rows if in stats or query for them
  async getGraphedRows(stats, graphedRows, resetSearch) {
    const missingRows = [];
    let graphedRowData = [];

    // Format graphedRows if it is list of edit IDs
    if (graphedRows.length && !graphedRows[0][this.rowIdentifier]) {
      graphedRows = graphedRows.map(editId => {
        return { [this.rowIdentifier]: editId };
      });
    }

    graphedRows.forEach(row => {
      const correspondingStat = stats.find(stat => {
        if (this.rowIdentifier === 'auction_id') {
          // AuctionIDs are > JS MAX_SAFE_INTEGER, so we can't compare using parseInt()
          return stat[this.rowIdentifier] === row[this.rowIdentifier];
        }
        return parseInt(stat[this.rowIdentifier]) === parseInt(row[this.rowIdentifier]);
      });
      if (correspondingStat) {
        graphedRowData.push(correspondingStat);
      } else {
        missingRows.push(row[this.rowIdentifier]);
      }
    });

    if (missingRows.length) {
      const params = {
        ...this.getRequestParams(),
        [`${this.rowIdentifier}s`]: missingRows.join(','),
      };
      const queriedRows = await this.service.queryGraphedRows(
        this.orgId,
        this.statType,
        params,
        resetSearch,
      );
      graphedRowData = graphedRowData.concat(queriedRows);
    }

    if (graphedRowData.length) {
      return Promise.resolve(graphedRowData);
    }
    return Promise.resolve([]);
  }

  // Daily / Weekly and Monthly / Yearly share calls, only call API when there is change
  granularitiesChanged(prevGranularities, currentGranularities) {
    const comparisonValues = [];
    [prevGranularities, currentGranularities].forEach((granularities, index) => {
      const selected = granularities.find(item => item.selected).name;
      comparisonValues[index] = selected === 'daily' || selected === 'weekly' ? 0 : 1;
    });
    return comparisonValues[0] !== comparisonValues[1];
  }

  /* Param helpers */
  async checkForEditQuery() {
    let queried = false;
    const urlParams = new URLSearchParams(this.props.location.search);
    const editQuery = urlParams.get('search_query');

    if (
      (editQuery && editQuery !== this.state.params.search_query) ||
      (!editQuery && this.state.params.search_query)
    ) {
      await this.getQueryParams(true);
      await this.queryStats(true);
      this.updateQueryParams();
      this.queryEditGraphStats(true);
      this.queryTotals();
      queried = true;
    }

    return queried;
  }

  async getQueryParams(resetPage = false) {
    // Get params from query string and update them in the state
    const urlParams = new URLSearchParams(this.props.location.search);

    await this.setState(currentState => {
      const [
        newParams,
        page,
        perPage,
        networks,
        granularities,
        merchants,
        publishers,
      ] = this.service.getQueryParams(urlParams, currentState, this.statType);

      // Don't assign graph query params to state
      const paramsExcludingGraph = { ...newParams };
      delete paramsExcludingGraph.graphed_ids;
      delete paramsExcludingGraph.granularity;

      const savedParams = this.Session.dashboardParams;
      const columns = this.columnService.setColumnsSelected(
        this.allColumns,
        this.statType,
        savedParams,
      );

      const updatedState = {
        params: { ...cloneDeep(currentState.params), ...paramsExcludingGraph },
        columns,
        page: resetPage ? 1 : page,
        perPage,
      };

      if (networks) updatedState.networks = networks;
      if (merchants) updatedState.merchants = merchants;
      if (publishers) updatedState.publishers = publishers;
      if (granularities) {
        updatedState.granularities = this.updateDisabledGranularities(
          granularities,
          newParams.date_from,
          newParams.date_to,
        );
      }

      if (newParams.graphed_ids) {
        updatedState.queriedGraphedIds = newParams.graphed_ids.split(',').map(id => {
          return parseInt(id);
        });
      }
      return updatedState;
    });
  }

  updateColumnList() {
    const columns = this.columnService.getColumnsForTable(
      this.statType,
      this.Session.isOrgAdmin,
      false,
      this.props.flags,
    );
    this.allColumns = columns;

    this.setState({
      columns: this.columnService.setColumnsSelected(
        columns,
        this.statType,
        this.Session.dashboardParams,
      ),
    });
  }

  updateQueryParams() {
    // Update the URL with current stat params
    const params = {
      per_page: this.state.perPage,
      page: this.state.page,
      ...this.getRequestParams(),
      ...this.getGraphParams(),
    };

    // If the user has navigated away from the page, do not replace history
    if (!this._isMounted) return;

    this.lastQueryParams = true;
    this.props.history.replace({
      pathname: this.props.location.pathname,
      search: buildQueryString(params),
    });
  }

  updateSortParams(columnName, columnLabel) {
    if (this.sortDisabledFields.includes(columnName)) return;

    const params = cloneDeep(this.state.params);

    /*
     * Default sorting to descending if user sorts by a different numeric column.
     */
    if (params.order_by !== columnName) {
      params.order_by = columnName;
      params.direction = this.defaultAscendingFields.includes(columnName) ? 'asc' : 'desc';
    } else {
      // Disable ascending sort on numeric columns without querying stats again
      if (
        this.statType === 'merchant' &&
        !['attribution_model', 'pricing_model', 'merchant_name'].includes(columnName)
      )
        return;

      params.direction = params.direction === 'desc' ? 'asc' : 'desc';
    }

    const gaDirection = params.direction === 'desc' ? 'Descending' : 'Ascending';
    const gaLabel = `${this.tableName} Header Sort ${columnLabel} ${gaDirection}`;
    this.AnalyticService.trackEvent('Mega Tables', 'Click', gaLabel);

    this.setState(
      {
        params,
        page: 1,
      },
      async () => {
        this.updateQueryParams();
        await this.queryStats(true, false, true);
        this.queryEditGraphStats(true);
      },
    );
  }

  getRequestParams(isTotal = false) {
    return this.service.getRequestParams(this.state, this.statType, isTotal, this.partnerType);
  }

  getEditGraphRequestParams(editId, auctionId) {
    const params = {
      edit_id: editId,
      date_from: this.state.params.date_from,
      date_to: this.state.params.date_to,
      granularity: this.state.granularities.find(item => item.selected).name,
      networks: this.service.getNetworkString(this.state.networks),
      merch_ids: this.service.getPartnerIds(this.state.merchants, 'merch'),
      pub_ids: this.service.getPartnerIds(this.state.publishers, 'pub'),
    };

    if (this.statType === 'merchantRecommendation' && auctionId) {
      params.auction_id = auctionId;
    }

    return params;
  }

  getGraphParams() {
    if (this.statType !== 'article') {
      return {};
    }

    const granularity = this.state.granularities.find(item => item.selected).name;
    const graphedIds = this.state.graphedRows.map(row => row[this.rowIdentifier]);
    return {
      granularity,
      graphed_ids: graphedIds,
    };
  }

  /* Helpers for child components */
  toggleGraph() {
    this.setState({ showGraph: !this.state.showGraph }, () => {
      const gaLabel = `${this.tableName} Graph ${this.state.showGraph ? 'Open' : 'Close'}`;
      this.AnalyticService.trackEvent('Mega Tables', 'Click', gaLabel);
      if (this.state.showGraph) {
        this.queryEditGraphStats();
      }
    });
  }

  getDateRange() {
    // Return current date objects for DatePicker
    return {
      startDate: dateService.dateFromStr(this.state.params.date_from),
      endDate: dateService.dateFromStr(this.state.params.date_to),
    };
  }

  setDateRange(startDate, endDate) {
    // Update state with new date params after user modifies DatePicker
    const clonedState = cloneDeep(this.state);
    const dateParams = {
      date_from: startDate,
      date_to: endDate,
    };

    const dashboardParams = this.Session.dashboardParams || {};
    this.Session.update({ dashboardParams: Object.assign(dashboardParams, dateParams) });

    this.setState(
      {
        params: { ...this.state.params, ...dateParams },
        granularities: this.updateDisabledGranularities(
          clonedState.granularities,
          startDate,
          endDate,
        ),
        // Reset page after changing dates
        page: 1,
        graphStats: null,
      },
      async () => {
        this.updateQueryParams();
        await this.queryStats(true);
        this.queryEditGraphStats(true);
        this.queryTotals();
      },
    );
  }

  resetColumns() {
    const columns = this.columnService.selectDefaultColumns(this.state.columns, this.statType);
    this.setState({ columns });
  }

  updateColumns(columns) {
    this.setState({ columns });
    const columnString = columns
      .filter(column => column.selected)
      .map(column => column.name)
      .join(',');

    const dashboardParams = this.Session.dashboardParams || {};
    Object.assign(dashboardParams, { [`${this.statType}_columns`]: columnString });
    this.Session.update({ dashboardParams });
  }

  updateNetworks(updatedNetworks) {
    this.setState({
      networks: updatedNetworks,
      page: 1, // Reset page after changing networks
    });
  }

  paginationUpdated(name) {
    return value => {
      this.setState({ [name]: value });
      // When changing perPage, reset page to 1 to avoid page mismatch
      if (name === 'perPage') {
        this.setState({ page: 1 });
      }
    };
  }

  onGranularitySelected(granularities) {
    this.setState(
      {
        granularities,
      },
      () => {
        this.updateQueryParams();
        this.queryEditGraphStats();
      },
    );
  }

  updateGraphedState(row, add = true) {
    const gaName =
      this.statType === 'merchantRecommendation' ? row.recommended_product : row.edit_name;
    if (this.state.graphedRows.length >= this.maxGraphedRows && add) {
      const gaLabel = `${this.tableName} Unable to Add Selection ${gaName}`;
      this.AnalyticService.trackEvent('Mega Tables', 'Click', gaLabel);
      return;
    }

    const actionLabel = add ? 'Add Selection to' : 'Remove Selection from';
    const gaLabel = `${this.tableName} ${actionLabel} Visualization ${gaName}`;
    this.setState(
      state => {
        let copy = cloneDeep(state).graphedRows;

        if (add) {
          copy.push(row);
        } else {
          copy = copy.filter(item => !isEqual(item, row));
        }
        this.AnalyticService.trackEvent('Mega Tables', 'Click', gaLabel);
        return { graphedRows: copy };
      },
      () => {
        this.queryEditGraphStats();
        this.updateQueryParams();
      },
    );
  }

  updateDisabledGranularities(granularities, startDate, endDate) {
    const numDays = dateService.subtractDates(endDate, startDate, 'day');
    const updatedGranularities = cloneDeep(granularities);

    updatedGranularities.forEach(item => {
      item.disabled =
        (item.name === 'weekly' && numDays < 14) ||
        (item.name === 'monthly' && numDays < 60) ||
        (item.name === 'yearly' && numDays < 365);

      if (item.disabled) {
        item.selected = false;
      }
    });

    if (!updatedGranularities.filter(item => item.selected).length) {
      // Select the last (largest) granularity that isn't disabled
      last(updatedGranularities.filter(item => !item.disabled)).selected = true;
    }

    return updatedGranularities;
  }

  render() {
    const partnersDropdown =
      this.state[this.partnerType].length > 0 ? (
        <PartnerDropdown
          gaEvent={{
            category: 'Mega Tables',
            label: `${this.tableName} ${this.formattedOrgType}`,
          }}
          items={this.state[this.partnerType]}
          updatePartners={partners =>
            this.setState({
              [this.partnerType]: partners,
              page: 1,
            })
          }
          statType={this.statType}
          staticFields={[]}
          location={this.props.location}
          orgType={this.partnerType}
          dropdownLabel={this.formattedOrgType}
        />
      ) : null;
    /*
     * Currently we don't support network filter for product stats.
     * We still send networks in the query but backend ignores it.
     */
    const networksDropdown = ![
      'product',
      'merchantArticle',
      'merchantRecommendation',
      'merchant',
      'merchantTopPublisher',
    ].includes(this.statType) ? (
      <NetworksDropdown
        gaEvent={{
          category: 'Mega Tables',
          label: `${this.tableName} Network`,
        }}
        items={this.state.networks}
        updateNetworks={networks => this.updateNetworks(networks)}
        statType={this.statType}
      />
    ) : null;
    const granularitiesDropdown = this.state.showGraph ? (
      <GranularitiesDropdown
        gaEvent={{
          category: 'Mega Tables',
          label: `${this.tableName} Granularity`,
        }}
        items={this.state.granularities}
        updateGranularities={granularities => this.onGranularitySelected(granularities)}
        statType={this.statType}
        showGraph={this.state.showGraph}
      />
    ) : null;
    const graphBtn = ['merchantArticle', 'merchantRecommendation', 'article'].includes(
      this.statType,
    ) ? (
      <div
        className={`graph-btn ${this.state.showGraph ? 'active' : ''}`}
        onClick={this.toggleGraph.bind(this)}
      >
        <img src={graphIcon} alt="graph icon" />
      </div>
    ) : null;
    const reportDropdown = !session.allSites ? (
      <ReportDropdown
        statType={this.statType}
        orgId={this.orgId}
        gaEvent={{
          category: 'Mega Tables',
          label: `${this.tableName} Export Data`,
        }}
        queryParams={this.getRequestParams()}
        columns={this.state.columns}
        currentPage={this.state.page}
        perPage={this.state.perPage}
        rowCounts={this.state.rowCounts}
        disabled={this.state.loading || this.state.showNoResult}
        reportVersion={this.statType === 'merchantTopPublisher' ? 'V2' : ''}
      ></ReportDropdown>
    ) : null;
    const tableHeader = (
      <div className="megatable-header">
        <div className="dropdown-header">
          <div className="table-name">{this.tableName}</div>
          <div className="dropdowns">
            {granularitiesDropdown}
            <DatePicker
              eventPrefix={`${this.tableName} Date Picker`}
              setDateRange={this.setDateRange.bind(this)}
              dateRange={this.getDateRange()}
              allowFutureDates={true}
            ></DatePicker>
            {partnersDropdown}
            {networksDropdown}
            <ColumnsDropdown
              gaEvent={{
                category: 'Mega Tables',
                label: `${this.tableName} Column`,
              }}
              items={this.state.columns}
              updateColumns={columns => this.updateColumns(columns)}
              resetColumns={this.resetColumns.bind(this)}
              statType={this.statType}
              staticFields={this.staticFields}
            ></ColumnsDropdown>
            {graphBtn}
            {reportDropdown}
          </div>
        </div>
        <Breadcrumbs
          history={this.props.history}
          location={this.props.location}
          tableName={this.tableName}
          service={this.service}
        />
      </div>
    );
    const graph = this.state.showGraph ? (
      <MegatableGraph
        stats={this.state.graphStats}
        eventPrefix={this.tableName}
        pubId={this.orgId}
        granularity={this.state.granularities.filter(item => item.selected)[0].name}
        dates={{
          date_from: this.state.params.date_from,
          date_to: this.state.params.date_to,
        }}
      />
    ) : null;
    const table =
      !this.state.loading && !this.state.showNoResult ? (
        <StatTable
          stats={this.state.stats}
          totals={this.state.totals}
          columns={this.state.columns}
          sortDisabledFields={this.sortDisabledFields}
          statType={this.statType}
          tableName={this.tableName}
          sortOptions={this.state.params}
          disableEditUrl={this.disableEditUrl}
          showTotals={!this.state.loading && this.state.totals}
          updateSortParams={this.updateSortParams.bind(this)}
          showGraphedRows={this.state.showGraph}
          graphedRows={this.state.graphedRows}
          maxGraphedRows={this.maxGraphedRows}
          updateGraphedState={this.updateGraphedState.bind(this)}
          history={this.props.history}
          match={this.props.match}
          location={this.props.location}
          graphKey={this.rowIdentifier}
        ></StatTable>
      ) : null;

    return (
      <>
        <div
          className={getClass(`base-megatable ${this.statType} fs-unmask`, {
            graphed: this.state.showGraph,
          })}
        >
          {tableHeader}
          <Loader loading={this.state.loading} />
          <NoResult
            type={`megatable-${this.statType}s`}
            noResult={!this.state.loading && this.state.showNoResult}
          />
          {graph}
          {table}
        </div>

        <Pagination
          showPagination={!this.state.showNoResult}
          currentPage={this.state.page}
          totalItems={this.state.totalItems}
          perPage={this.state.perPage}
          eventPrefix={`${this.tableName} Mega Tables`}
          updatePage={this.paginationUpdated('page')}
          updatePerPage={this.paginationUpdated('perPage')}
        ></Pagination>
      </>
    );
  }
}

export default Megatable;
